/**
 * Persistent game progression. Lives in localStorage so the retreat crowd can
 * close the tab and come back. Everything the flow needs to gate scenes
 * (stars, sheepies, finale) is derived from here.
 */

export type LevelId = "dan" | "lucas" | "andrew";
export const LEVEL_ORDER: LevelId[] = ["dan", "lucas", "andrew"];

export interface Progress {
  stars: Record<LevelId, number>; // 0..3 per level
  sheepies: Record<LevelId, boolean>; // hidden collectible found?
  finaleCleared: boolean;
  // Leaderboard run stats, kept as the BEST result ever achieved per level so a
  // replay that does better raises the totals (see recordLevelResult).
  bestScore: Record<LevelId, number>; // highest points scored in the level
  bestCollectibles: Record<LevelId, number>; // most collectibles grabbed in one run
  fastestTimeMs: Record<LevelId, number>; // fastest clear, ms of active play; 0 = none yet
}

/** The per-level result recorded on a successful clear (see BaseLevelScene). */
export interface LevelResult {
  score: number;
  collectibles: number;
  timeMs: number;
}

const STORAGE_KEY = "codeandco.progress.v1";

function fresh(): Progress {
  return {
    stars: { dan: 0, lucas: 0, andrew: 0 },
    sheepies: { dan: false, lucas: false, andrew: false },
    finaleCleared: false,
    bestScore: { dan: 0, lucas: 0, andrew: 0 },
    bestCollectibles: { dan: 0, lucas: 0, andrew: 0 },
    fastestTimeMs: { dan: 0, lucas: 0, andrew: 0 },
  };
}

class GameStateClass {
  private data: Progress;

  constructor() {
    this.data = this.load();
  }

  private load(): Progress {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return fresh();
      const parsed = JSON.parse(raw) as Partial<Progress>;
      // merge over a fresh object so missing keys never crash the game
      const base = fresh();
      return {
        stars: { ...base.stars, ...parsed.stars },
        sheepies: { ...base.sheepies, ...parsed.sheepies },
        finaleCleared: parsed.finaleCleared ?? false,
        bestScore: { ...base.bestScore, ...parsed.bestScore },
        bestCollectibles: { ...base.bestCollectibles, ...parsed.bestCollectibles },
        fastestTimeMs: { ...base.fastestTimeMs, ...parsed.fastestTimeMs },
      };
    } catch {
      return fresh();
    }
  }

  private save() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.data));
    } catch {
      /* storage disabled - fine, progress just won't persist */
    }
  }

  get progress(): Readonly<Progress> {
    return this.data;
  }

  stars(id: LevelId): number {
    return this.data.stars[id];
  }

  /** Record stars, keeping the best result ever achieved. */
  recordStars(id: LevelId, stars: number) {
    this.data.stars[id] = Math.max(this.data.stars[id], Math.min(3, Math.max(0, stars)));
    this.save();
  }

  /** Record a level's run stats, keeping the best result ever achieved: the most
   *  points, the most collectibles, and the fastest active-play time. Called on a
   *  successful clear so replaying a level to do better raises the run totals. */
  recordLevelResult(id: LevelId, result: LevelResult) {
    this.data.bestScore[id] = Math.max(this.data.bestScore[id], Math.max(0, result.score));
    this.data.bestCollectibles[id] = Math.max(this.data.bestCollectibles[id], Math.max(0, result.collectibles));
    const t = Math.max(0, Math.round(result.timeMs));
    // 0 means "no time yet", so the first clear seeds it; later clears take the min.
    this.data.fastestTimeMs[id] = this.data.fastestTimeMs[id] === 0 ? t : Math.min(this.data.fastestTimeMs[id], t);
    this.save();
  }

  /** Total points across all levels, summing each level's best run. */
  totalPoints(): number {
    return LEVEL_ORDER.reduce((sum, id) => sum + this.data.bestScore[id], 0);
  }

  /** Total collectibles across all levels, summing each level's best run. */
  totalCollectibles(): number {
    return LEVEL_ORDER.reduce((sum, id) => sum + this.data.bestCollectibles[id], 0);
  }

  /** Total active-play time across all levels (ms), summing each level's fastest clear. */
  totalTimeMs(): number {
    return LEVEL_ORDER.reduce((sum, id) => sum + this.data.fastestTimeMs[id], 0);
  }

  foundSheepy(id: LevelId) {
    this.data.sheepies[id] = true;
    this.save();
  }

  hasSheepy(id: LevelId): boolean {
    return this.data.sheepies[id];
  }

  sheepyCount(): number {
    return LEVEL_ORDER.filter((id) => this.data.sheepies[id]).length;
  }

  allSheepies(): boolean {
    return this.sheepyCount() === LEVEL_ORDER.length;
  }

  /** A level is playable once the previous one has at least one star. */
  isUnlocked(id: LevelId): boolean {
    const i = LEVEL_ORDER.indexOf(id);
    if (i <= 0) return true;
    const prev = LEVEL_ORDER[i - 1];
    return this.data.stars[prev] > 0;
  }

  /** Finale requires three stars on all three levels (the Gate). */
  finaleUnlocked(): boolean {
    return LEVEL_ORDER.every((id) => this.data.stars[id] >= 3);
  }

  setFinaleCleared() {
    this.data.finaleCleared = true;
    this.save();
  }

  /** True once the American market (the relay) has been won. */
  americaWon(): boolean {
    return this.data.finaleCleared;
  }

  totalStars(): number {
    return LEVEL_ORDER.reduce((sum, id) => sum + this.data.stars[id], 0);
  }

  reset() {
    this.data = fresh();
    this.save();
  }

  /** Dev helper for testing the flow quickly. */
  grantEverything() {
    this.data = {
      stars: { dan: 3, lucas: 3, andrew: 3 },
      sheepies: { dan: true, lucas: true, andrew: true },
      finaleCleared: false,
      bestScore: { dan: 320, lucas: 280, andrew: 400 },
      bestCollectibles: { dan: 8, lucas: 6, andrew: 10 },
      fastestTimeMs: { dan: 42000, lucas: 55000, andrew: 38000 },
    };
    this.save();
  }
}

export const GameState = new GameStateClass();
